import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, AlertTriangle, Link2 } from "lucide-react";
import type { PageIssue, BrokenExternalDetails } from "@/types/technicalAudit";
import { sanitizeBrokenUrl } from "./issueBeforeAfter";

interface BrokenExternalCardProps {
	issue: PageIssue;
}

function getStatusColor(code: number): string {
	if (code >= 500) return "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800";
	if (code >= 400) return "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800";
	return "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800";
}

export function BrokenExternalCard({ issue }: BrokenExternalCardProps) {
	const details = issue.issue_details as BrokenExternalDetails;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/40">
					<AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-red-900 dark:text-red-300">Broken External Link</h4>
					<p className="text-sm mt-1 text-red-700 dark:text-red-400">
						An outbound link on this page returns a {details.status_code} status. Broken external links hurt user trust and page quality signals.
					</p>
				</div>
				<Badge variant="outline" className={getStatusColor(details.status_code)}>
					{details.status_code}
				</Badge>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
							<ExternalLink className="w-4 h-4 text-red-600 dark:text-red-400" />
						</div>
						Broken URL
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<div className="p-3 rounded-lg border bg-red-50 dark:bg-red-950/40/50 border-red-200 dark:border-red-800">
						<p className="text-sm font-mono break-all text-red-700 dark:text-red-400">
							{sanitizeBrokenUrl(details.target_url)}
						</p>
					</div>
					{details.link_text && (
						<p className="text-xs text-muted-foreground mt-2">
							Anchor text: &ldquo;{details.link_text}&rdquo;
						</p>
					)}
				</CardContent>
			</Card>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Link2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Source Page
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<a
						href={details.page_url}
						onClick={(e) => openWithCacheBuster(e, details.page_url)}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
					>
						<p className="text-sm font-mono break-all text-foreground flex-1">
							{details.page_url}
						</p>
						<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
				</CardContent>
			</Card>
		</div>
	);
}
